import { ChevronRight, DownloadOutlined } from "@mui/icons-material";
import { Avatar, Chip, darken, IconButton, Stack, Typography } from "@mui/material";
import { MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDateTime } from "~/modules/app/util/formatters";
import { getFileTypeIcon } from "~/modules/media/utils/mediaUtils";

import { MapEditorObservationPanelObservationMediaPointFragment } from "./fragments.generated";

export type ObservationMediaPointCardProps = {
    createHref: (element: MapEditorObservationPanelObservationMediaPointFragment) => string;
    mediaPoint: MapEditorObservationPanelObservationMediaPointFragment;
};

export function ObservationMediaPointCard({ createHref, mediaPoint }: ObservationMediaPointCardProps) {
    const { t } = useTranslation("planner");

    const handleDownloadClick: MouseEventHandler<HTMLButtonElement> = event => {
        event.preventDefault();

        if (mediaPoint.media.url === null) return;
        window.open(mediaPoint.media.url, "_blank");
    };

    const FileIcon = getFileTypeIcon(mediaPoint.media.mimeType);
    const speciesInObservationMediaPoint = uniqueSpeciesInObservationMediaPoint(mediaPoint);

    return (
        <Stack
            component={Link}
            to={createHref(mediaPoint)}
            relative="path"
            sx={theme => ({
                padding: 2,
                border: `1px solid ${theme.palette.divider}`,
                rowGap: 1,
                color: theme.palette.text.primary,
                textDecoration: "none",
                "&:hover": {
                    backgroundColor: darken(theme.palette.background.paper, 0.03),
                },
            })}
        >
            <Stack alignItems="center" direction="row" gap={2} justifyContent="space-between">
                <Avatar sx={theme => ({ backgroundColor: theme.palette.primary.main })} variant="rounded">
                    {FileIcon}
                </Avatar>
                <Stack flex={1} minWidth={0}>
                    <Typography overflow="hidden" textOverflow="ellipsis">
                        {mediaPoint.media.name}
                    </Typography>
                    <Typography overflow="hidden" textOverflow="ellipsis">
                        {formatDateTime(parseStringToDate(mediaPoint.createdAt))}
                    </Typography>
                </Stack>
                <Stack alignItems="center" direction="row" gap={1}>
                    {mediaPoint.media.url !== null ? (
                        <IconButton onClick={handleDownloadClick}>
                            <DownloadOutlined />
                        </IconButton>
                    ) : (
                        <Typography variant="caption">{t("plannerMediaPreviewDialog.notUploaded")}</Typography>
                    )}
                    <ChevronRight />
                </Stack>
            </Stack>
            {speciesInObservationMediaPoint.length > 0 && (
                <Stack direction="row" gap={1} flexWrap="wrap">
                    {speciesInObservationMediaPoint.map(detection => (
                        <Chip key={detection} label={detection} />
                    ))}
                </Stack>
            )}
        </Stack>
    );
}

function uniqueSpeciesInObservationMediaPoint(input: MapEditorObservationPanelObservationMediaPointFragment): string[] {
    const elements = input.observationMediaPointSpecies.map(x => x.species.name);

    return [...new Set(elements)];
}
